import { extractJpegMetadata } from '@kop/media';
/** Detect the encoded file, not the extension or the photo picker's sometimes-empty MIME. */
export async function albumPhotoType(file: Blob): Promise<string | null> {
  const b = new Uint8Array(await file.slice(0, 256).arrayBuffer());
  const ascii = (start: number, end: number) => String.fromCharCode(...b.slice(start, end));
  if (b[0] === 255 && b[1] === 216 && b[2] === 255) return 'image/jpeg';
  if ([137,80,78,71,13,10,26,10].every((v,i)=>b[i]===v)) return 'image/png';
  if (ascii(0,4)==='RIFF' && ascii(8,12)==='WEBP') return 'image/webp';
  if (ascii(4,8)==='ftyp') {
    const size = new DataView(b.buffer).getUint32(0);
    const brands = [ascii(8,12)];
    for (let i=16;i+4<=Math.min(size,b.length);i+=4) brands.push(ascii(i,i+4));
    if (!brands.some(brand=>['avif','avis'].includes(brand)) &&
      brands.some(brand=>['heic','heix','hevc','hevx','heim','heis','mif1','msf1'].includes(brand))) return 'image/heic';
  }
  return null;
}

let conversionTail: Promise<unknown> = Promise.resolve();
/** Prefer the device decoder before loading the software HEIC decoder. */
async function nativeHeicJpeg(file: Blob): Promise<Blob> {
  if(typeof Image==='undefined'||typeof document==='undefined')throw new Error('IMAGE_NATIVE_DECODE_UNAVAILABLE');
  const url=URL.createObjectURL(file.slice(0,file.size,'image/heic'));
  const image=new Image();
  const canvas=document.createElement('canvas');
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    image.decoding='async';image.src=url;
    await Promise.race([image.decode(),new Promise<never>((_resolve,reject)=>{
      timer=setTimeout(()=>reject(new Error('IMAGE_NATIVE_DECODE_TIMEOUT')),15000);
    })]);
    if(!image.naturalWidth||!image.naturalHeight)throw new Error('IMAGE_DECODE_FAILED');
    const ratio=Math.min(1,2560/Math.max(image.naturalWidth,image.naturalHeight));
    canvas.width=Math.max(1,Math.round(image.naturalWidth*ratio));
    canvas.height=Math.max(1,Math.round(image.naturalHeight*ratio));
    const context=canvas.getContext('2d');if(!context)throw new Error('CANVAS_CONTEXT_UNAVAILABLE');
    context.drawImage(image,0,0,canvas.width,canvas.height);
    const result=await new Promise<Blob>((resolve,reject)=>canvas.toBlob(blob=>blob?resolve(blob):reject(new Error('IMAGE_ENCODE_FAILED')),'image/jpeg',0.9));
    if(await albumPhotoType(result)!=='image/jpeg')throw new Error('IMAGE_CONVERSION_FAILED');
    return result;
  } finally {
    clearTimeout(timer);image.src='';URL.revokeObjectURL(url);canvas.width=0;canvas.height=0;
  }
}
async function resizeLargeJpeg(file: Blob): Promise<Blob> {
  const bytes=new Uint8Array(await file.slice(0,1024*1024).arrayBuffer());
  let width=0,height=0;
  for(let i=2;i+8<bytes.length;){
    if(bytes[i]!==255) break;
    const marker=bytes[i+1]!;
    if(marker===255){i++;continue;}
    if(marker===217 || marker===218) break;
    const length=bytes[i+2]!*256+bytes[i+3]!;
    if(length<2) break;
    if([192,193,194,195,197,198,199,201,202,203,205,206,207].includes(marker)){
      height=bytes[i+5]!*256+bytes[i+6]!;width=bytes[i+7]!*256+bytes[i+8]!;break;
    }
    i+=length+2;
  }
  if (!width || !height || (Math.max(width,height)<=4096 && file.size<=15*1024*1024)) return file;
  // Request decoder-side downsampling before allocating a full-resolution phone canvas.
  const scale=Math.min(1,2560/Math.max(width,height));
  const orientation=extractJpegMetadata(bytes).orientation;
  if(orientation>=5) [width,height]=[height,width];
  let source:CanvasImageSource; let w:number; let h:number; let close:()=>void;
  try {
    const bitmap=await createImageBitmap(file,{imageOrientation:'from-image',resizeWidth:Math.max(1,Math.round(width*scale)),resizeHeight:Math.max(1,Math.round(height*scale)),resizeQuality:'high'});
    const ratio=Math.min(1,2560/Math.max(bitmap.width,bitmap.height));
    source=bitmap;w=Math.max(1,Math.round(bitmap.width*ratio));h=Math.max(1,Math.round(bitmap.height*ratio));close=()=>bitmap.close();
  } catch {
    const url=URL.createObjectURL(file);
    const img=new Image();img.src=url;
    try {await img.decode();} catch(error){URL.revokeObjectURL(url);throw error;}
    const ratio=Math.min(1,2560/Math.max(img.naturalWidth,img.naturalHeight));
    source=img;w=Math.round(img.naturalWidth*ratio);h=Math.round(img.naturalHeight*ratio);
    close=()=>{img.src='';URL.revokeObjectURL(url);};
  }
  const canvas=document.createElement('canvas');
  try {
    canvas.width=w;canvas.height=h;
    const context=canvas.getContext('2d');if(!context)throw new Error('CANVAS_CONTEXT_UNAVAILABLE');
    context.drawImage(source,0,0,w,h);
    return await new Promise<Blob>((resolve,reject)=>canvas.toBlob(b=>b?resolve(b):reject(new Error('IMAGE_ENCODE_FAILED')),'image/jpeg',0.9));
  } finally {close();canvas.width=0;canvas.height=0;}
}
/** Keep software HEIC conversion bounded: multiple album photos must not decode together. */
export async function normalizeAlbumPhoto(file: Blob, allowHeic: boolean): Promise<Blob> {
  if (!file.size || file.size > 50*1024*1024) throw new Error('MEDIA_PREFLIGHT_REJECTED');
  const type = await albumPhotoType(file);
  if (!type) throw new Error('IMAGE_SOURCE_TYPE_UNSUPPORTED');
  if (type !== 'image/heic') {
    const typed=file.type===type?file:file.slice(0,file.size,type);
    if(type!=='image/jpeg' || !allowHeic) return typed;
    const pending=conversionTail.catch(()=>undefined).then(()=>resizeLargeJpeg(typed));
    conversionTail=pending.catch(()=>undefined);return pending;
  }
  if (!allowHeic) throw new Error('IMAGE_ORIGINAL_FORMAT_UNSUPPORTED');
  const pending = conversionTail.catch(()=>undefined).then(async()=>{
    try { return await nativeHeicJpeg(file); } catch {
      // Older devices and other browsers can still use the bounded software fallback.
    }
    const {heicTo} = await import('heic-to/csp');
    const converted = await heicTo({blob:file,type:'image/jpeg',quality:0.9});
    if (!(converted instanceof Blob) || await albumPhotoType(converted) !== 'image/jpeg')
      throw new Error('IMAGE_CONVERSION_FAILED');
    return resizeLargeJpeg(converted);
  });
  conversionTail = pending.catch(()=>undefined);
  return pending;
}
